// ChatPreferencesModal — the operator's session-default editor for the
// chat orchestrator. Same fields the chat tool's `set_chat_preferences`
// accepts; same shape PromptComposer's right-side config exposes per ticket.
//
// On open, loads /chat/preferences and seeds the form with stored OR
// effective values (built-in defaults shown when nothing's stored). Save
// PATCHes a partial — only fields the operator actually touched. Validation
// surfaces the server's per-field error inline.

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { ChatPreferences, ChatPreferencesResponse } from '../lib/types';

const PROVIDER_OPTIONS = ['anthropic', 'openai', 'codex-oauth', 'google', 'openrouter'];

interface ChatPreferencesModalProps {
  onClose: () => void;
}

type FormState = {
  default_agent_provider: string;
  default_agent_model: string;
  default_review_provider: string;
  default_review_model: string;
  default_execution_mode: 'autonomous' | 'assisted';
  default_review_loops: string;
  default_repo_url: string;
  default_repo_path: string;
  default_max_budget_usd: string;
  bulk_queue_threshold: string;
  auto_confirm_bulk: boolean;
};

const EMPTY_FORM: FormState = {
  default_agent_provider: '',
  default_agent_model: '',
  default_review_provider: '',
  default_review_model: '',
  default_execution_mode: 'autonomous',
  default_review_loops: '',
  default_repo_url: '',
  default_repo_path: '',
  default_max_budget_usd: '',
  bulk_queue_threshold: '',
  auto_confirm_bulk: false,
};

// Parse an optional numeric field. Blank → null (the patch will clear it);
// a non-finite number (paste of "12abc" / "NaN" past the type="number" UI
// guard) → error so the user sees the typo before submit instead of after a
// round-trip. Pure, module-scoped, and testable in isolation.
type ParsedNumber =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

function parseOptionalNumber(raw: string, label: string): ParsedNumber {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    return { ok: false, error: `${label}: "${trimmed}" is not a valid number` };
  }
  return { ok: true, value: n };
}

// Parse one optional-number form field and stage the resulting diff onto
// `patch`. Returns an error string when the input is malformed; null on
// success (whether or not the field actually changed). Collapsing the
// per-field "parse → guard → diff" boilerplate to one call site means
// adding a fourth numeric field is one line instead of seven.
function applyOptionalNumberField<K extends keyof ChatPreferences>(
  patch: ChatPreferences,
  key: K,
  raw: string,
  label: string,
  storedValue: number | null | undefined,
): string | null {
  const parsed = parseOptionalNumber(raw, label);
  if (!parsed.ok) return parsed.error;
  if (parsed.value === null) {
    // Only stage a clear when there's a real value to clear. `undefined`
    // means the field was never set; `null` means it was already cleared.
    // In either case, sending {key: null} again is a no-op patch — skip it
    // so we don't pad the PATCH body with redundant nulls.
    if (storedValue !== undefined && storedValue !== null) {
      (patch as Record<string, unknown>)[key] = null;
    }
  } else if (parsed.value !== storedValue) {
    (patch as Record<string, unknown>)[key] = parsed.value;
  }
  return null;
}

export function ChatPreferencesModal({ onClose }: ChatPreferencesModalProps) {
  const [loaded, setLoaded] = useState<ChatPreferencesResponse | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getChatPreferences();
        if (cancelled) return;
        setLoaded(res);
        // Pre-fill form from effective (stored merged with built-in defaults)
        // so a fresh tenant sees sensible starting values, not blanks.
        setForm({
          default_agent_provider: res.effective.default_agent_provider ?? '',
          default_agent_model: res.effective.default_agent_model ?? '',
          default_review_provider: res.effective.default_review_provider ?? '',
          default_review_model: res.effective.default_review_model ?? '',
          default_execution_mode: res.effective.default_execution_mode ?? 'autonomous',
          default_review_loops: res.effective.default_review_loops != null
            ? String(res.effective.default_review_loops) : '',
          default_repo_url: res.effective.default_repo_url ?? '',
          default_repo_path: res.effective.default_repo_path ?? '',
          default_max_budget_usd: res.effective.default_max_budget_usd != null
            ? String(res.effective.default_max_budget_usd) : '',
          bulk_queue_threshold: String(res.effective.bulk_queue_threshold ?? ''),
          auto_confirm_bulk: !!res.effective.auto_confirm_bulk,
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    // Build a partial patch: only include fields that diverge from the
    // currently-stored value (so unrelated stored fields aren't disturbed).
    // Empty-string string fields are sent as null to explicitly clear.
    const stored = loaded?.stored ?? {};
    const patch: ChatPreferences = {};
    const setString = (k: keyof ChatPreferences, v: string, storedV: unknown): void => {
      if (v.trim().length === 0) {
        if (storedV !== undefined) (patch as Record<string, unknown>)[k] = null;
      } else if (v.trim() !== storedV) {
        (patch as Record<string, unknown>)[k] = v.trim();
      }
    };
    setString('default_agent_provider', form.default_agent_provider, stored.default_agent_provider);
    setString('default_agent_model', form.default_agent_model, stored.default_agent_model);
    setString('default_review_provider', form.default_review_provider, stored.default_review_provider);
    setString('default_review_model', form.default_review_model, stored.default_review_model);
    setString('default_repo_url', form.default_repo_url, stored.default_repo_url);
    setString('default_repo_path', form.default_repo_path, stored.default_repo_path);

    if (form.default_execution_mode !== stored.default_execution_mode) {
      patch.default_execution_mode = form.default_execution_mode;
    }

    // Numeric fields: blank → null (clear), otherwise Number() the input.
    // `type="number"` on the input element prevents most invalid entries,
    // but a paste of "12abc" or "NaN" can slip through and `Number(...)`
    // would silently produce NaN — which the server would either reject
    // or store as a useless value. Validate client-side and bail with the
    // first parse error so the user can fix the typo before submit, instead
    // of seeing a generic API error after the round-trip.
    const numericError =
      applyOptionalNumberField(patch, 'default_review_loops',
        form.default_review_loops, 'Review loops', stored.default_review_loops) ??
      applyOptionalNumberField(patch, 'default_max_budget_usd',
        form.default_max_budget_usd, 'Max budget USD', stored.default_max_budget_usd) ??
      applyOptionalNumberField(patch, 'bulk_queue_threshold',
        form.bulk_queue_threshold, 'Bulk queue threshold', stored.bulk_queue_threshold);
    if (numericError) {
      setSaveError(numericError);
      setSaving(false);
      return;
    }

    if (form.auto_confirm_bulk !== !!stored.auto_confirm_bulk) {
      patch.auto_confirm_bulk = form.auto_confirm_bulk;
    }

    if (Object.keys(patch).length === 0) {
      setSaving(false);
      setSavedAt('no changes');
      return;
    }
    try {
      const res = await api.updateChatPreferences(patch);
      setLoaded(res);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : err instanceof Error ? err.message : String(err);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-head">
          <h3>chat orchestrator defaults</h3>
          <button className="icon-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="mono" style={{ fontSize: 12, opacity: 0.75 }}>
            Session defaults the orchestrator applies when a ticket leaves a field unset.
            Per-ticket values in chat always win — these just fill gaps.
          </p>

          {loadError && (
            <div className="msg-system mono" style={{ color: 'var(--accent-warn, #d97706)' }}>
              failed to load preferences: {loadError}
            </div>
          )}

          {loaded && (
            <>
              <Section title="agent + reviewer">
                <Row>
                  <SelectField
                    label="agent provider"
                    value={form.default_agent_provider}
                    onChange={(v) => setForm((f) => ({ ...f, default_agent_provider: v }))}
                    options={['', ...PROVIDER_OPTIONS]}
                  />
                  <TextField
                    label="agent model"
                    value={form.default_agent_model}
                    onChange={(v) => setForm((f) => ({ ...f, default_agent_model: v }))}
                    placeholder="alias (e.g. sonnet, haiku, gpt5)"
                  />
                </Row>
                <Row>
                  <SelectField
                    label="reviewer provider"
                    value={form.default_review_provider}
                    onChange={(v) => setForm((f) => ({ ...f, default_review_provider: v }))}
                    options={['', ...PROVIDER_OPTIONS.filter((p) => p !== form.default_agent_provider)]}
                  />
                  <TextField
                    label="reviewer model"
                    value={form.default_review_model}
                    onChange={(v) => setForm((f) => ({ ...f, default_review_model: v }))}
                    placeholder="alias (e.g. sonnet, haiku, gemini-2-5-pro)"
                  />
                </Row>
              </Section>

              <Section title="ticket defaults">
                <Row>
                  <SelectField
                    label="mode"
                    value={form.default_execution_mode}
                    onChange={(v) => setForm((f) => ({ ...f, default_execution_mode: v as 'autonomous' | 'assisted' }))}
                    options={['autonomous', 'assisted']}
                  />
                  <TextField
                    label="review loops"
                    type="number"
                    value={form.default_review_loops}
                    onChange={(v) => setForm((f) => ({ ...f, default_review_loops: v }))}
                    placeholder="0-10"
                  />
                </Row>
                <Row>
                  <TextField
                    label="default repo URL"
                    value={form.default_repo_url}
                    onChange={(v) => setForm((f) => ({ ...f, default_repo_url: v }))}
                    placeholder="https://github.com/owner/repo"
                  />
                  <TextField
                    label="default repo path"
                    value={form.default_repo_path}
                    onChange={(v) => setForm((f) => ({ ...f, default_repo_path: v }))}
                    placeholder="/abs/path/to/checkout"
                  />
                </Row>
                <Row>
                  <TextField
                    label="max budget (USD)"
                    type="number"
                    value={form.default_max_budget_usd}
                    onChange={(v) => setForm((f) => ({ ...f, default_max_budget_usd: v }))}
                    placeholder="e.g. 2 — blank = no cap"
                  />
                </Row>
              </Section>

              <Section title="bulk-queue confirmation">
                <Row>
                  <TextField
                    label="ask at N tickets"
                    type="number"
                    value={form.bulk_queue_threshold}
                    onChange={(v) => setForm((f) => ({ ...f, bulk_queue_threshold: v }))}
                    placeholder="default 3"
                  />
                  <label className="composer-select" style={{ alignSelf: 'end', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={form.auto_confirm_bulk}
                      onChange={(e) => setForm((f) => ({ ...f, auto_confirm_bulk: e.target.checked }))}
                    />
                    <span className="composer-select-label mono" style={{ marginBottom: 0 }}>
                      auto-confirm bulk (skip the gate)
                    </span>
                  </label>
                </Row>
              </Section>

              {saveError && (
                <div className="msg-system mono" style={{ color: 'var(--accent-warn, #d97706)' }}>
                  {saveError}
                </div>
              )}
              {savedAt && !saveError && (
                <div className="msg-system mono" style={{ opacity: 0.6 }}>
                  {savedAt === 'no changes' ? 'no changes' : `saved at ${savedAt}`}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="draft-btn-sec" onClick={onClose}>cancel</button>
                <button className="draft-btn-pri" onClick={() => { void save(); }} disabled={saving}>
                  {saving ? 'saving…' : 'save'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div className="mono" style={{ fontSize: 11, opacity: 0.55, marginBottom: 6 }}>
        {props.title}
      </div>
      {props.children}
    </div>
  );
}

function Row(props: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
      {props.children}
    </div>
  );
}

function SelectField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="composer-select" style={{ flex: 1 }}>
      <span className="composer-select-label mono">{props.label}</span>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        {props.options.map((o) => (
          <option key={o || '_blank'} value={o}>{o || '(use built-in default)'}</option>
        ))}
      </select>
    </label>
  );
}

function TextField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'number';
}) {
  return (
    <label className="composer-select" style={{ flex: 1 }}>
      <span className="composer-select-label mono">{props.label}</span>
      <input
        className="mono"
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
      />
    </label>
  );
}
